import globals from "@homer.core/globals";
import StorageSession from "@homer.core/storage.session";
import application from "@homer.core/application";
import { name } from "../package.json";

const storageSession = new StorageSession(name, { version: application.version });

const nonDigits = /\D/g;
const optionalNationalPrefix = /\(\s*0\s*\)/;
const leadingZeros = /^0+/;
const internationalPrefix = /^\+|^00/;
const internationalNumber = /^[1-9]\d{7,14}$/;
const countryCodePattern = /^\+?[1-9]\d{0,2}$/;

// Reuse the stateless encoder across identity updates.
const textEncoder = new TextEncoder();

const sha256 = async (value) => {
    const msgBuffer = textEncoder.encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);

    return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
};

class Phone {
    #method;
    #plain;
    #hash;

    constructor({ phone, hash, method }) {
        this.#method = method || undefined;
        this.#plain = phone;
        this.#hash = hash;
    }

    // countryCode is required, e.g. "41" or "+41"; there is no implicit home country.
    async set(phone, countryCode, method = undefined, enforce = false) {
        if (!countryCodePattern.test(countryCode)) {
            throw new TypeError("A country calling code is required (e.g. 41 or +41)");
        }

        if (phone) {
            // Only +/00 marks international input; national input requires the supplied country code.
            // Keep the canonical international representation vendor-independent, including its leading +.
            phone = phone.trim();
            const isInternational = internationalPrefix.test(phone);
            // Remove the optional national prefix before stripping its identifying parentheses.
            if (isInternational) phone = phone.replace(optionalNationalPrefix, "");
            phone = phone.replace(nonDigits, "").replace(leadingZeros, "");
            if (!phone) return;
            if (!isInternational) phone = `${String(countryCode).replace(nonDigits, "")}${phone}`;
            if (!internationalNumber.test(phone)) return;
            phone = `+${phone}`;

            if (enforce || method !== this.#method || phone !== this.#plain) {
                const hash = phone !== this.#plain ? await sha256(phone) : this.#hash;

                storageSession.set(JSON.stringify({ phone, hash, method: method || undefined }));

                this.#method = method || undefined;
                this.#plain = phone;
                this.#hash = hash;

                // Checkout awaits persistence only; loading and preparing tracking must not delay navigation.
                void globals.demand
                    .request("@unifold/event.user.identify")
                    .then(([UserIdentifyEvent]) => {
                        return new UserIdentifyEvent({ method: method || "unknown", phone: hash }).dispatch();
                    })
                    .catch((error) => {
                        console.error("Phone identification failed", error);
                    });
            }
        } else {
            storageSession.remove();

            this.#method = undefined;
            this.#plain = undefined;
            this.#hash = undefined;
        }
    }

    get method() {
        return this.#method;
    }

    get plain() {
        return this.#plain;
    }

    get hash() {
        return this.#hash;
    }
}

export default new Phone(JSON.parse(storageSession.get() || "{}"));
